import React from 'react';
import PropTypes from 'prop-types';

/**
 * Column - A simple HTML replacement for Bloomer's Column component
 * Uses the same class names as Bloomer for perfect CSS compatibility
 */
export function Column({
  isSize = null,
  isOffset = null,
  isNarrow = false,
  isMobile = false,
  isDesktop = false,
  hasTextAlign = null,
  hasTextColor = null,
  isPaddingless = false,
  className = '',
  style = {},
  children = null,
  ...rest
}) {
  // Build the class name string based on props
  let classNames = 'column';
  
  // Handle paddingless
  if (isPaddingless) {
    classNames += ' is-paddingless';
  }
  
  // Handle size classes
  if (isSize) {
    if (isSize === 'full') {
      classNames += ' is-full';
    } else if (isSize === 'narrow') {
      classNames += ' is-narrow';
    } else if (typeof isSize === 'string' && isSize.includes('/')) {
      // Handle fraction sizes like '1/2', '1/3', etc.
      const [numerator, denominator] = isSize.split('/');
      
      // Map to proper Bulma class names
      if (numerator === '1' && denominator === '2') {
        classNames += ' is-half';
      } else if (numerator === '1' && denominator === '3') {
        classNames += ' is-one-third';
      } else if (numerator === '2' && denominator === '3') {
        classNames += ' is-two-thirds';
      } else if (numerator === '1' && denominator === '4') {
        classNames += ' is-one-quarter';
      } else if (numerator === '3' && denominator === '4') {
        classNames += ' is-three-quarters';
      } else if (numerator === '1' && denominator === '5') {
        classNames += ' is-one-fifth';
      } else if (numerator === '2' && denominator === '5') {
        classNames += ' is-two-fifths';
      } else if (numerator === '3' && denominator === '5') {
        classNames += ' is-three-fifths';
      } else if (numerator === '4' && denominator === '5') {
        classNames += ' is-four-fifths';
      } else {
        // Fallback for unsupported fractions
        classNames += ` is-${Math.round((numerator / denominator) * 12)}`;
      }
    } else {
      // Handle numeric sizes
      classNames += ` is-${isSize}`;
    }
  }
  
  // Handle offset classes
  if (isOffset) {
    if (typeof isOffset === 'string' && isOffset.includes('/')) {
      // Handle fraction offsets like '1/2', '1/3', etc.
      const [numerator, denominator] = isOffset.split('/');
      classNames += ` is-offset-${numerator}-${denominator}`;
    } else {
      // Handle numeric offsets
      classNames += ` is-offset-${isOffset}`;
    }
  }
  
  // Handle narrow columns
  if (isNarrow) {
    classNames += ' is-narrow';
  }
  
  // Handle responsive modifiers
  if (isMobile) {
    classNames += ' is-mobile';
  }
  
  if (isDesktop) {
    classNames += ' is-desktop';
  }
  
  // Add any additional classes
  if (className) {
    classNames += ` ${className}`;
  }
  // Handle text alignment prop (convert to Bulma class)
  if (hasTextAlign) {
    classNames += ` has-text-${hasTextAlign}`;
  }

  // Handle text color prop (convert to Bulma class)
  if (hasTextColor) {
    classNames += ` has-text-${hasTextColor}`;
  }

  // Strip custom props that shouldn't be forwarded to DOM
  const { hasTextAlign: _hta, hasTextColor: _htc, isPaddingless: _ip, ...domProps } = rest;

  return (
    <div className={classNames} style={style} {...domProps}>
      {children}
    </div>
  );
}

// eslint-disable-next-line react/require-default-props
Column.propTypes = {
  isSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isOffset: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isNarrow: PropTypes.bool,
  isMobile: PropTypes.bool,
  isDesktop: PropTypes.bool,
  hasTextAlign: PropTypes.string,
  hasTextColor: PropTypes.string,
  isPaddingless: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
  children: PropTypes.node,
};



export default Column;
